"use client"

import { useState, useEffect } from "react"
import {
  MagnifyingGlass,
  FunnelSimple,
  X,
  ArrowClockwise,
  WarningCircle,
  SpinnerGap,
  CalendarBlank,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  PlusCircle,
  Eye,
  User,
  CreditCard,
  FileText,
  CurrencyDollar,
  PencilSimple,
  TrashSimple,
  Tag,
  Activity,
  CaretDown,
  ArrowDownRight,
  ArrowUpRight,
} from "phosphor-react"

import { DateRangePicker, SearchableSelect, DeleteModal } from "../../components"
import ExpenseModal from "./ExpenseModal"
import ExpenseDetailModal from "./ExpenseDetailModal"
import expensesService from "../../services/expensesService"
import creditAccountsService from "../../services/creditAccountsService"
import clientsService from "../../services/clientsService"
import { useCurrentUser } from "../../utils/context/UserProvider"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import toast from "react-hot-toast"

function ExpenseManagement() {
  const { currentUser } = useCurrentUser()
  const { checkAccess } = useAccessChecker()

  const isAdmin = ["Admin", "SuperAdmin"].includes(currentUser?.role?.name)
  const isClient = currentUser?.role?.name === "Client"

  const canCreate = checkAccess("expenses", "create")
  const canUpdate = checkAccess("expenses", "update")
  const canDelete = checkAccess("expenses", "delete")

  // Data
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)

  // Filters
  const [searchQuery, setSearchQuery] = useState("")
  const [dateRange, setDateRange] = useState({ start: "", end: "" })
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [filters, setFilters] = useState({
    client: "",
    type: "",
    creditAccount: "",
    direction: "", // NEW: debit / credit
  })

  // Options
  const [accountOptions, setAccountOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])

  // Modals & state
  const [showExpenseModal, setShowExpenseModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedExpense, setSelectedExpense] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [totals, setTotals] = useState({
    totalCredit: 0,
    totalDebit: 0,
  })
  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalPages: 1,
    totalItems: 0,
  })

  const [filtersActive, setFiltersActive] = useState(false)
  const [done, setDone] = useState(false)

  /* ───────────────────── Page title ───────────────────── */
  useEffect(() => {
    const event = new CustomEvent("setPageTitle", {
      detail: { title: "Asset Management" },
    })
    window.dispatchEvent(event)

    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Dashboard" },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [])

  /* ───────────────────── Filters active? ───────────────────── */
  useEffect(() => {
    const hasActive =
      searchQuery !== "" ||
      dateRange.start !== "" ||
      dateRange.end !== "" ||
      filters.client !== "" ||
      filters.type !== "" ||
      filters.creditAccount !== "" ||
      filters.direction !== ""

    setFiltersActive(hasActive)
  }, [searchQuery, dateRange, filters])

  /* ───────────────────── Fetches ───────────────────── */

  useEffect(() => {
    fetchExpenses()

    if (isAdmin) {
      fetchCreditAccounts()
      fetchClients()
    } else if (isClient && currentUser?._id) {
      fetchClientCreditAccounts(currentUser._id)

      setFilters((prev) => ({
        ...prev,
        client: currentUser._id || "",
      }))
    }

    setIsEditing(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, pagination.currentPage, pagination.pageSize, currentUser])

  const fetchExpenses = async () => {
    try {
      setLoading(true)

      const clientFilter = isClient ? currentUser?._id : filters.client

      const payload = {
        ...filters,
        client: clientFilter,
        searchValue: searchQuery,
        dateRange,
        pagination: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        },
      }

      const response = await expensesService.search(payload)
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {
        setExpenses(responseData.data || [])
        // ✅ NEW: totals for cards (from backend search)
        if (responseData.totals) {
          setTotals({
            totalCredit: Number(responseData.totals.totalCredit) || 0,
            totalDebit: Number(responseData.totals.totalDebit) || 0,
          })
        } else {
          setTotals({ totalCredit: 0, totalDebit: 0 })
        }

        if (responseData.pagination) {
          setPagination({
            currentPage: responseData.pagination.currentPage,
            pageSize: responseData.pagination.pageSize,
            totalPages: responseData.pagination.totalPages,
            totalItems: responseData.pagination.totalItems,
          })
        }
      }
    } catch (error) {
      console.error("Failed to fetch expenses:", error)
      toast.error(error.response?.data?.message || "Failed to load expenses")
    } finally {
      setLoading(false)
    }
  }

  const fetchCreditAccounts = async () => {
    try {
      setLoadingOptions(true)

      const response = await creditAccountsService.searchAccounts()
      const responseData = response.data

      if (responseData && responseData.statusCode === 200 && Array.isArray(responseData.data)) {
        const dataAccounts = responseData.data
          .filter((item) => item && item._id)
          .map((item) => ({
            id: item._id,
            name: item.account_name || "Unknown Account",
            value: item._id,
            balance: item.balance || 0,
            currency: item.account_currency || "USD",
            clientName: item.user?.reference || item.user?.fullName || "",
          }))

        setAccountOptions(dataAccounts)
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error)
      toast.error(error.response?.data?.message || "Failed to load credit accounts")
    } finally {
      setLoadingOptions(false)
    }
  }

  const fetchClientCreditAccounts = async (clientId) => {
    if (!clientId) return

    try {
      setLoadingOptions(true)

      const response = await creditAccountsService.searchAccounts(clientId)
      const responseData = response.data

      if (responseData && responseData.statusCode === 200 && Array.isArray(responseData.data)) {
        const dataAccounts = responseData.data
          .filter((item) => item && item._id)
          .map((item) => ({
            id: item._id,
            name: item.account_name || "Unknown Account",
            value: item._id,
            balance: item.balance || 0,
            currency: item.account_currency || "USD",
            clientName: item.user?.reference || item.user?.fullName || "",
          }))

        setAccountOptions(dataAccounts)
      }
    } catch (error) {
      console.error("Failed to fetch client accounts:", error)
      toast.error(error.response?.data?.message || "Failed to load credit accounts")
    } finally {
      setLoadingOptions(false)
    }
  }

  const fetchClients = async () => {
    try {
      setLoadingOptions(true)

      const response = await clientsService.searchClients()
      const responseData = response.data

      if (responseData && responseData.statusCode === 200 && Array.isArray(responseData.data)) {
        const dataClients = responseData.data
          .filter((item) => item && item._id)
          .map((item) => ({
            id: item._id,
            name: item.reference || item.fullName || "Unknown Client",
            value: item._id,
          }))

        setUserOptions(dataClients)
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error)
      toast.error(error.response?.data?.message || "Failed to load clients")
    } finally {
      setLoadingOptions(false)
    }
  }

  /* ───────────────────── Helpers ───────────────────── */

  const typeOptions = [
    { id: "ad account purchase", value: "ad account purchase", name: "Ad Account Purchase" },
    { id: "setup", value: "setup", name: "Setup" },
    { id: "other", value: "other", name: "Other" },
  ]

  const directionOptions = [
    { id: "debit", value: "debit", name: "Debit (Expense)" },
    { id: "credit", value: "credit", name: "Credit (Adjustment)" },
  ]

  const formattedUserOptions = [
    { id: 0, name: "All Clients", value: "" },
    ...userOptions.map((u, index) => ({
      id: index + 1,
      name: u.name,
      value: u.value,
    })),
  ]

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }))
  }

  const handleDateRangeChange = (range) => setDateRange(range)

  const clearFilters = () => {
    if (isClient) {
      setFilters({
        creditAccount: "",
        client: currentUser?._id || "",
        type: "",
        direction: "",
      })
    } else {
      setFilters({
        creditAccount: "",
        client: "",
        type: "",
        direction: "",
      })
    }

    setSearchQuery("")
    setDateRange({ start: "", end: "" })
  }

  const applyFilters = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }))
    fetchExpenses()
  }

  const resetAndRefresh = () => {
    clearFilters()
    setPagination((p) => ({ ...p, currentPage: 1 }))
    setTimeout(() => {
      fetchExpenses()
    }, 0)
  }

  const handleSearch = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }))
    fetchExpenses()
  }

  const handleCreate = () => {
    setSelectedExpense(null)
    setIsEditing(false)
    setShowExpenseModal(true)
  }

  const handleViewExpense = (expense) => {
    setSelectedExpense(expense)
    setShowDetailModal(true)
  }

  const handleEditExpense = (expense) => {
    setSelectedExpense(expense)
    setIsEditing(true)
    setShowExpenseModal(true)
  }

  const handleDeleteExpense = (id) => {
    setDeleteId(id)
    setDeleteModal(true)
  }

  const handleFormSuccess = () => {
    setDone((prev) => !prev)
  }

  const onCloseModals = () => {
    setShowExpenseModal(false)
    setShowDetailModal(false)
    setTimeout(() => {
      setSelectedExpense(null)
      setIsEditing(false)
    }, 300)
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return
    setPagination((p) => ({ ...p, currentPage: newPage }))
  }

  const handlePageSizeChange = (e) => {
    const newSize = Number.parseInt(e.target.value)
    setPagination((p) => ({ ...p, pageSize: newSize, currentPage: 1 }))
  }

  const formatCurrency = (amount, currency = "USD") => {
    const num = Number(amount) || 0
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(num)
  }

  const formatDate = (raw) => {
    if (!raw) return "N/A"
    const dateString = String(raw).trim()
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const dotDateRE =
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
    const hasExplicitTime =
      /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString)

    let d
    const m = dateString.match(dotDateRE)
    if (m) {
      const [, dd, mm, yyyy, HH, MM] = m
      d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        HH ? parseInt(HH, 10) : 0,
        MM ? parseInt(MM, 10) : 0,
        0,
        0
      )
    } else {
      d = new Date(dateString)
    }
    if (isNaN(d.getTime())) return raw

    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone,
    }).format(d)

    if (!hasExplicitTime) return datePart

    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    })

    const parts = timeFormatter.formatToParts(d)
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00"
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00"

    const hourNum = parseInt(hour, 10)
    const suffix = hourNum < 12 ? "AM" : "PM"

    const timePart = `${hour}:${minute} ${suffix}`

    return `${datePart}, ${timePart}`
  }

  const getExpenseTypeColor = (type) => {
    const colorMap = {
      marketing: "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
      office: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
      travel: "bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300",
      software: "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300",
      equipment: "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300",
      utilities: "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300",
      consulting: "bg-pink-50 text-pink-700 dark:bg-pink-900/20 dark:text-pink-300",
      other: "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/20 dark:text-neutral-300",
      "ad account purchase": "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
      setup: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
    }
    return (
      colorMap[type] ||
      "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/20 dark:text-neutral-300"
    )
  }

  const getDirectionBadge = (direction) => {
    if (direction === "credit") {
      return {
        label: "Credit (Adjustment)",
        className:
          "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
        Icon: ArrowUpRight,
      }
    }
    // default debit
    return {
      label: "Debit (Expense)",
      className:
        "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-500",
      Icon: ArrowDownRight,
    }
  }

  const getPageNumbers = (currentPage, totalPages) => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
    if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages]
    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    }
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
  }
  const displayCurrency = "USD"
  /* ───────────────────── Render ───────────────────── */

  return (
    <div className="lg:p-7 p-3 max-w-full mx-auto">

      {/* ✅ NEW: Summary Cards (Deposit / Refund) */}
      {
        currentUser?.role?.name !== 'Client' ?
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <SummaryCard
              title="Total Credit (Adjustment) - Out"
              value={totals.totalCredit}
              currency={displayCurrency}
              icon={<ArrowDownRight size={20} />}
              tone="green"
            />
            <SummaryCard
              title="Total Debit (Expense) - In"
              value={totals.totalDebit}
              currency={displayCurrency}
              icon={<ArrowUpRight size={20} />}
              tone="red"
            />

          </div>
          :
          <div className="grid grid-cols-1  mb-6">

            <SummaryCard
              title="Total Credit (Adjustment) - Gifts"
              value={totals.totalCredit}
              currency={displayCurrency}
              icon={<ArrowDownRight size={20} />}
              tone="green"
            />
          </div>
      }


      {/* Top bar */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        {/* Search */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>
          <input
            type="text"
            placeholder="Search assets..."
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 cursor-pointer ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={handleSearch}
            aria-label="Submit search"
          >
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Date range */}
        <DateRangePicker
          onRangeChange={handleDateRangeChange}
          value={dateRange}
          className={
            dateRange.start || dateRange.end
              ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
              : ""
          }
        />

        {/* More filters */}
        <button
          className={`flex items-center gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${showMoreFilters
            ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
            : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
            }`}
          onClick={() => setShowMoreFilters((v) => !v)}
        >
          <span className="text-sm">{showMoreFilters ? "Hide filters" : "More filters"}</span>
          {showMoreFilters ? (
            <X size={16} className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        {/* Reset */}
        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">Reset</span>
        </button>

        {/* Apply filters (inline) */}
        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-sm justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-sm">Apply Filters</span>
          </button>
        )}

        {/* Create asset (admin only) */}
        {isAdmin && canCreate && (
          <button
            className="ml-auto flex items-center gap-2 px-4 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
            onClick={handleCreate}
          >
            <PlusCircle size={16} />
            <span className="text-sm">Create Asset</span>
          </button>
        )}
      </div>

      {/* Active filters chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">Active filters:</span>

          {searchQuery && (
            <Chip
              icon={<MagnifyingGlass size={12} />}
              label={searchQuery}
              onClear={() => {
                setSearchQuery("")
                handleSearch()
              }}
            />
          )}

          {(dateRange.start || dateRange.end) && (
            <Chip
              icon={<CalendarBlank size={12} />}
              label={`${dateRange.start ? new Date(dateRange.start).toLocaleDateString() : "Any"} - ${dateRange.end ? new Date(dateRange.end).toLocaleDateString() : "Any"
                }`}
              onClear={() => {
                setDateRange({ start: "", end: "" })
                handleSearch()
              }}
            />
          )}

          {filters.creditAccount && (
            <Chip
              icon={<CreditCard size={12} />}
              label={
                accountOptions.find((acc) => acc.value === filters.creditAccount)?.name ||
                filters.creditAccount
              }
              onClear={() => {
                handleFilterChange("creditAccount", "")
                applyFilters()
              }}
            />
          )}

          {isAdmin && filters.client && (
            <Chip
              icon={<User size={12} />}
              label={
                userOptions.find((u) => u.value === filters.client)?.name ||
                filters.client
              }
              onClear={() => {
                handleFilterChange("client", "")
                applyFilters()
              }}
            />
          )}

          {filters.type && (
            <Chip
              icon={<FileText size={12} />}
              label={filters.type}
              onClear={() => {
                handleFilterChange("type", "")
                applyFilters()
              }}
            />
          )}

          {filters.direction && (
            <Chip
              icon={<Activity size={12} />}
              label={filters.direction === "credit" ? "Credit (Adjustment)" : "Debit (Expense)"}
              onClear={() => {
                handleFilterChange("direction", "")
                applyFilters()
              }}
            />
          )}

          <button
            onClick={resetAndRefresh}
            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* More filters panel */}
      {showMoreFilters && (
        <div className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">More filters</h2>
            <button
              onClick={clearFilters}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
            >
              <X size={14} />
              <span>Clear all</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Client (admin only) */}
            {isAdmin && (
              <SearchableSelect
                options={formattedUserOptions}
                value={filters.client}
                onChange={(v) => handleFilterChange("client", v)}
                placeholder="Client"
                icon={<User size={16} />}
                isLoading={loadingOptions}
              />
            )}

            {/* Asset Type */}
            <SearchableSelect
              options={typeOptions}
              value={filters.type}
              onChange={(v) => handleFilterChange("type", v)}
              placeholder="Asset Type"
              icon={<FileText size={16} />}
            />

            {/* Direction */}
            <SearchableSelect
              options={directionOptions}
              value={filters.direction}
              onChange={(v) => handleFilterChange("direction", v)}
              placeholder="Flow (Debit or Credit)"
              icon={<Activity size={16} />}
            />

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
                onClick={applyFilters}
              >
                <FunnelSimple size={16} />
                <span className="text-sm">Apply Filters</span>
              </button>
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                onClick={resetAndRefresh}
              >
                <ArrowClockwise size={16} />
                <span className="text-sm">Reset</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table + pagination */}
      <div className="border border-neutral-150 dark:border-neutral-900 rounded-lg bg-white dark:bg-neutral-950">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap
              size={30}
              weight="bold"
              className="animate-spin text-neutral-600 dark:text-neutral-400"
            />
          </div>
        ) : expenses.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-t-xl">
              <table className="min-w-full divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50/80 backdrop-blur-sm dark:bg-neutral-900/40">
                  <tr>
                    <Th>Date & Time</Th>
                    {isAdmin && <Th>Client Reference</Th>}

                    <Th>Asset Name</Th>
                    {/* <Th>Asset Type</Th> */}
                    <Th>Flow Type</Th>
                    <Th>Amount</Th>
                    {isAdmin && <Th>Done By</Th>}
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-neutral-950 divide-y divide-neutral-100 dark:divide-neutral-800 text-start">
                  {expenses.map((expense) => {
                    const dirMeta = getDirectionBadge(expense.direction)
                    const isCredit = expense.direction === "credit"
                    const sign = isCredit ? "+" : "-"

                    return (
                      <tr
                        key={expense._id}
                        className="hover:bg-neutral-50/80 dark:hover:bg-neutral-900/60 transition-colors"
                      >

                        {/* Date */}
                        <Td>
                          <span className="text-sm whitespace-nowrap text-neutral-900 dark:text-white">
                            {formatDate(expense.createdAt)}
                          </span>
                        </Td>

                        {/* Client (admin only) */}
                        {isAdmin && (
                          <Td>
                            <div className="text-sm whitespace-nowrap text-neutral-900 dark:text-white">
                              {expense.client?.reference || expense.client?.fullName || "N/A"}
                            </div>
                            {expense.client?.email && (
                              <div className="text-xs whitespace-nowrap text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                                {expense.client.email}
                              </div>
                            )}
                          </Td>
                        )}

                        {/* Asset title / note */}
                        <Td>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-neutral-100 dark:bg-neutral-900/60">
                              <FileText size={16} className="text-neutral-600 dark:text-neutral-300" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm line-clamp-1 max-w-[150px] truncate font-[400] text-neutral-900 dark:text-white truncate">
                                {expense.title}
                              </div>
                              {expense.note && (
                                <div className="text-xs  line-clamp-1 max-w-[150px] truncate text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                                  {expense.note}
                                </div>
                              )}
                            </div>
                          </div>
                        </Td>

                        {/* Type */}
                        {/* <Td>
                          <span
                            className={`inline-flex  whitespace-nowrap items-center px-4 py-1 capitalize rounded-full text-xs font-[400] ${getExpenseTypeColor(
                              expense.type
                            )}`}
                          >
                            <Tag size={12} className="mr-1.5" />
                            <span className="line-clamp-1 max-w-[150px] truncate">{expense.type}</span>
                          </span>
                        </Td> */}

                        {/* Flow (direction) */}
                        <Td>
                          <span
                            className={`inline-flex whitespace-nowrap items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${dirMeta.className}`}
                          >
                            <dirMeta.Icon size={12} />
                            {dirMeta.label}
                          </span>
                        </Td>

                        {/* Amount */}
                        <Td className="whitespace-nowrap">
                          <span
                            className={`font-[500] ${isCredit
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-500"
                              }`}
                          >
                            {sign}{" "}
                            {formatCurrency(
                              expense.amount || 0,
                              expense.credit_account?.account_currency || "USD"
                            )}
                          </span>
                        </Td>


                        {/* Done by (admin only) */}
                        {isAdmin && (
                          <Td>
                            <div className="text-sm text-neutral-900 dark:text-white">
                              {expense.admin?.fullName || expense.admin?.name || "System"}
                            </div>
                            {expense.admin?.email && (
                              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                                {expense.admin.email}
                              </div>
                            )}
                          </Td>
                        )}

                        {/* Actions */}
                        <Td className="text-sm font-[300]">
                          <div className="flex gap-2 items-center">
                            <button
                              onClick={() => handleViewExpense(expense)}
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-600/10 p-1.5 rounded-xl transition-colors"
                            >
                              <Eye size={14} />
                            </button>

                            {isAdmin && canUpdate && (
                              <button
                                onClick={() => handleEditExpense(expense)}
                                className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 bg-amber-600/10 p-1.5 rounded-xl transition-colors"
                              >
                                <PencilSimple size={14} />
                              </button>
                            )}

                            {isAdmin && canDelete && (
                              <button
                                onClick={() => handleDeleteExpense(expense._id)}
                                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 bg-red-600/10 p-1.5 rounded-xl transition-colors"
                              >
                                <TrashSimple size={14} />
                              </button>
                            )}
                          </div>
                        </Td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 bg-neutral-50/60 dark:bg-neutral-900/40 rounded-b-xl">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-[13px] text-neutral-600 dark:text-neutral-400">
                  Showing {expenses.length} of {pagination.totalItems} assets
                </span>

                <div className="ml-4 flex items-center gap-2">
                  <span className="text-[12px] text-neutral-500 dark:text-neutral-400">Rows</span>
                  <div className="relative">
                    <select
                      value={pagination.pageSize}
                      onChange={handlePageSizeChange}
                      className="h-8 pl-3 pr-8 text-[12px] border border-neutral-200/80 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-neutral-100 shadow-sm focus:outline-none focus:ring-1 focus:ring-neutral-300 dark:focus:ring-neutral-600 appearance-none cursor-pointer"
                    >
                      <option value={5}>5 / page</option>

                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </select>
                    <CaretDown
                      size={14}
                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(1)}
                  aria="First page"
                >
                  <CaretDoubleLeft size={14} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  aria="Previous page"
                >
                  <CaretLeft size={14} />
                </PageBtn>

                <div className="flex items-center space-x-1">
                  {getPageNumbers(pagination.currentPage, pagination.totalPages).map(
                    (page, idx) =>
                      page === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 py-1 text-neutral-500 dark:text-neutral-400"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-7 h-7 flex items-center justify-center rounded-xl text-[13px] ${pagination.currentPage === page
                            ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                            : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            }`}
                        >
                          {page}
                        </button>
                      ),
                  )}
                </div>

                <PageBtn
                  disabled={
                    pagination.currentPage === pagination.totalPages ||
                    pagination.totalPages === 0
                  }
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  aria="Next page"
                >
                  <CaretRight size={14} />
                </PageBtn>

                <PageBtn
                  disabled={
                    pagination.currentPage === pagination.totalPages ||
                    pagination.totalPages === 0
                  }
                  onClick={() => handlePageChange(pagination.totalPages)}
                  aria="Last page"
                >
                  <CaretDoubleRight size={14} />
                </PageBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>
            <h3 className="text-xl font-[400] text-neutral-900 dark:text-white mb-2">
              No Assets Found
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {searchQuery ||
                Object.values(filters).some(Boolean) ||
                dateRange.start ||
                dateRange.end
                ? "No assets match your search criteria. Try adjusting your filters or search term."
                : "There are no assets in the system yet. Create your first asset to get started."}
            </p>
            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />
                Reset Filters
              </button>

              {isAdmin && canCreate && (
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  <PlusCircle size={16} />
                  <span>Create Asset</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete modal */}
      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="expenses"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />

      {/* Asset form modal */}
      <ExpenseModal
        isOpen={showExpenseModal}
        onClose={onCloseModals}
        onSuccess={handleFormSuccess}
        expenseId={selectedExpense?._id}
        isEditing={isEditing}
      />

      {/* Asset details */}
      <ExpenseDetailModal
        isOpen={showDetailModal}
        onClose={onCloseModals}
        expense={selectedExpense}
      />
    </div>
  )
}

/* ───────────────────── Small UI helpers ───────────────────── */

function Th({ children }) {
  return (
    <th className="px-6 py-5 text-left text-[12px] font-[400] text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.08em] whitespace-nowrap">
      {children}
    </th>
  )
}

function Td({ children, className = "" }) {
  return (
    <td
      className={`px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-200 ${className}`}
    >
      {children}
    </td>
  )
}

function Chip({ label, onClear, icon }) {
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-sm">
      {icon}
      <span>{label}</span>
      <button onClick={onClear} className="ml-1">
        <X size={12} />
      </button>
    </div>
  )
}

function PageBtn({ disabled, onClick, children, aria }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1 rounded-xl ${disabled
        ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        }`}
      aria-label={aria}
    >
      {children}
    </button>
  )
}

/* ✅ NEW: Summary Card */
function SummaryCard({ title, value, currency, icon, tone = "neutral" }) {
   const toneMap = {
    green: "bg-white dark:bg-neutral-950 text-green-600 dark:text-green-500 border-neutral-150 dark:border-neutral-900",
    red: "bg-white dark:bg-neutral-950 text-red-600 dark:text-red-500 border-neutral-150 dark:border-neutral-900",
  }

  const pillMap = {
    green: "bg-green-600/10 text-green-700 dark:bg-green-500/10 dark:text-green-600",
    red: "bg-red-600/10 text-red-700 dark:bg-red-500/10 dark:text-red-600",
  }

  return (
    <div className={`border flex items-center justify-between rounded-xl p-5 ${toneMap[tone] || toneMap.neutral}`}>
      <div className="flex   flex-col justify-center items-start">
        <div className="text-xs uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
          {title}
        </div>
        <div className="mt-1 text-2xl font-[400]">
          <span>
            {Number(value || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>{" "}
          <span className="text-sm font-[400] ">
            {currency}
          </span>
        </div>

      </div>

      <div className={`p-3 rounded-xl ${pillMap[tone] || pillMap.neutral}`}>
        {icon}
      </div>
    </div>
  )
}
export default ExpenseManagement
